/**
 * Le denunce, e il loro percorso.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * QUESTI TIPI SONO STATI VERIFICATI CONTRO LA RISPOSTA VERA, non dedotti
 * dallo schema OpenAPI né dalle entità del backend. Due cose che lo schema
 * lasciava intendere diversamente:
 *
 *   `species` è una **stringa** con il codice (`"canine"`), non un oggetto e
 *   non un IRI: un normalizzatore dedicato la appiattisce in entrambe le
 *   direzioni. Vale anche in scrittura.
 *
 *   I campi nulli sono **assenti**, non `null`. `submitted_at`, `decided_at`,
 *   `rejection_reason`, `animal` e `parent_declaration` non compaiono affatto
 *   finché non hanno un valore. Per questo sono opzionali e non
 *   `string | null`: leggerli con `?.` è l'unico modo corretto.
 *
 * Quando i tipi saranno generati da `docs.json` questa nota diventerà
 * inutile — e sarà un buon giorno.
 * ─────────────────────────────────────────────────────────────────────────
 */

/** Prima iscrizione, monta, nascita, passaggio di proprietà, altro. */
export type DeclarationType = 'registration' | 'mating' | 'birth' | 'transfer' | 'other'

export type DeclarationStatus =
  | 'draft'
  | 'submitted'
  | 'review'
  | 'approved'
  | 'rejected'
  | 'registered'

export type DeclarationTransition =
  | 'submit'
  | 'review'
  | 'approve'
  | 'reject'
  | 'register'
  | 'reopen'

export interface Declaration {
  id: number
  /** Il codice della specie, non un oggetto: `"canine"`. */
  species: string
  type: DeclarationType
  /** Il protocollo, assegnato da noi: `2026/0001`. */
  number: string
  status: DeclarationStatus
  /**
   * Data e ora, non solo data: la colonna è una data ma la risposta porta
   * `2026-08-09T00:00:00+00:00`. Formattandola come stringa si vedrebbe
   * l'orario a mezzanotte.
   */
  declared_on: string
  declarant: { id: number; display_name: string }
  payload: Record<string, unknown>
  submitted_at?: string
  decided_at?: string
  rejection_reason?: string
  animal?: { id: number; name: string } | null
  parent_declaration?: { id: number; number: string } | null
}

/**
 * Le transizioni ammesse da ogni stato.
 *
 * Rispecchia `config/packages/workflow.yaml`: se una delle due cambia,
 * cambiano entrambe. Serve **solo a non mostrare pulsanti che daranno
 * errore**: il permesso e l'ammissibilità li decide comunque il server, che
 * risponde `409` nominando lo stato attuale se qualcuno prova lo stesso.
 */
const TRANSIZIONI: Readonly<Record<DeclarationStatus, readonly DeclarationTransition[]>> = {
  draft: ['submit'],
  submitted: ['review', 'approve', 'reject'],
  review: ['approve', 'reject'],
  approved: ['register'],
  rejected: ['reopen'],
  registered: [],
}

export function allowedTransitions(status: DeclarationStatus): readonly DeclarationTransition[] {
  return TRANSIZIONI[status]
}

/**
 * Lo stato in parole, per chi legge.
 *
 * «Da correggere» e non «Rifiutata»: la denuncia rifiutata torna in bozza e
 * si ripresenta, non è finita. La parola che si sceglie qui decide se chi la
 * riceve pensa di aver perso il lavoro o di doverlo sistemare.
 */
export function statusLabel(status: DeclarationStatus): string {
  return {
    draft: 'Bozza',
    submitted: 'Presentata',
    review: 'In esame',
    approved: 'Approvata',
    rejected: 'Da correggere',
    registered: 'Registrata',
  }[status]
}

export function typeLabel(type: DeclarationType): string {
  return {
    registration: 'Prima iscrizione',
    mating: 'Monta',
    birth: 'Nascita',
    transfer: 'Passaggio di proprietà',
    other: 'Altro',
  }[type]
}

/**
 * L'etichetta del pulsante di una transizione.
 *
 * Sono verbi all'infinito e non sostantivi: un pulsante dice cosa fa, non
 * come si chiama il passaggio.
 */
export function transitionLabel(transition: DeclarationTransition): string {
  return {
    submit: 'Presenta',
    review: 'Metti in esame',
    approve: 'Approva',
    reject: 'Respingi',
    register: 'Registra',
    reopen: 'Riporta in bozza',
  }[transition]
}

/**
 * `register` è l'unica transizione che tocca il registro: crea i soggetti,
 * chiude e apre le intestazioni, assegna i numeri. Va chiesta conferma, e la
 * conferma deve dire **cosa succederà**, non «sei sicuro?».
 */
export function isIrreversible(transition: DeclarationTransition): boolean {
  return transition === 'register'
}
